
import React from 'react';
import taurineGraph from '@/assets/taurine-enzyme-activity.png';
import betaineGraph from '@/assets/betaine-endurance-study.png';
import tyrosineGraph from '@/assets/tyrosine-brain-transport.png';

const OtherIngredientsSection = () => {
  return (
    <>
      {/* Betaine */}
      <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
        <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">Betaine Anhydrous – 5 000 mg</h3>
        <div className="bg-muted p-3 md:p-4 rounded-lg border border-border mb-3">
          <h4 className="font-bold text-primary mb-2 text-sm md:text-base">Klinisk effektiv dose: 2 500–6 000 mg. Peveo: 5 000 mg.</h4>
          <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
            Ifølge <a href="https://pubmed.ncbi.nlm.nih.gov/30320585/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">Harty et al. (2018)</a> er doseringer av betaine i vanlige PWO-er ofte betydelig lavere enn klinisk effektive doser (1,25–2,5g i studieprotokollene). Vi bruker 5g (dobbel studiedose) for å sikre respons hos alle.
          </p>
        </div>
        <p className="mb-3 text-sm md:text-base text-muted-foreground">
          Betaine fungerer som en osmolytt og bidrar til intracellulær hyperhydrering. 
          Ved å forbedre cellehydrering øker Betaine pumpen du oppnår under trening og kan forebygge dehydrering.
        </p>
        <div className="bg-muted p-3 md:p-4 rounded-lg">
          <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Forskningsresultater:</h4>
          <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
            <li>Signifikant økning av styrke og kraftproduksjon</li>
            <li>Økning i repetisjoner på leg press og benkpress</li>
            <li>Potensial for økt muskelvekst og fettforbrenning</li>
          </ul>
          <p className="mt-2 text-sm text-primary">
            Kilder: 
            <a href="https://pubmed.ncbi.nlm.nih.gov/23481424/" className="underline hover:text-primary ml-1" target="_blank" rel="noopener noreferrer">Cholewa et al. (2013)</a>,
            <a href="https://pubmed.ncbi.nlm.nih.gov/30320585/" className="underline hover:text-primary ml-1" target="_blank" rel="noopener noreferrer">Harty et al. (2018)</a>,
            <a href="https://pubmed.ncbi.nlm.nih.gov/20642826/" className="underline hover:text-primary ml-1" target="_blank" rel="noopener noreferrer">Lee et al. (2010)</a>
          </p>
        </div>

        <div className="bg-muted p-3 md:p-4 rounded-lg mt-3 md:mt-4 border border-border">
          <h4 className="font-bold text-foreground mb-3 text-sm md:text-base">Visualisering av betainets effekt på muskelutholdenhet</h4>
          <img 
            src={betaineGraph} 
            alt="Graf som viser betainets effekt på antall repetisjoner i benpress og benkpress" 
            className="max-w-md mx-auto rounded-lg mb-3"
          />
          <p className="text-xs md:text-sm text-muted-foreground">
            <span className="font-semibold text-primary">Figur fra Arazi et al. (2022):</span> Grafen demonstrerer at deltakere som tok betaine i 14 dager presterte flere repetisjoner på både benpress og benkpress ved 80% av 1RM sammenlignet med placebo.
          </p>
        </div>
      </div>

      {/* Taurine & Tyrosine grid */}
      <div className="grid md:grid-cols-2 gap-3 md:gap-6">
        <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
          <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">Taurine – 3 000 mg</h3>
          <div className="bg-muted p-3 md:p-4 rounded-lg border border-border mb-3">
            <h4 className="font-bold text-primary mb-2 text-sm md:text-base">Markedssnitt: ~1 211 mg. Peveo: 3 000 mg.</h4>
            <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
              Ifølge en meta-analyse av <a href="https://pubmed.ncbi.nlm.nih.gov/29546641/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">Waldron et al. (2018)</a> forbedrer doser på 1–6g taurin «Time-to-Exhaustion» signifikant. Med 3g ligger vi langt over industristandarden, optimalisert for hydrering og utholdenhet.
            </p>
          </div>
          <p className="mb-3 text-sm md:text-base text-muted-foreground">
            Taurin stabiliserer mitokondrielle funksjoner og fungerer som buffer i muskelceller. 
            Dette optimaliserer fettsyreforbrenning og utsetter tid til utmattelse under trening.
          </p>
          <div className="bg-muted p-3 md:p-4 rounded-lg">
            <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Forskningsresultater:</h4>
            <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
              <li>Utsetter tid til utmattelse hos både trente og utrente</li>
              <li>Optimaliserer fettsyreforbrenning i mitokondrier</li>
              <li>Forbedrer cellehydrering og muskelfunksjon</li>
              <li>Akutt virkning – effekt allerede første dose</li>
            </ul>
            <p className="mt-2 text-sm text-primary">
              Kilder: 
              <a href="https://pubmed.ncbi.nlm.nih.gov/29546641/" className="underline hover:text-primary ml-1" target="_blank" rel="noopener noreferrer">Waldron et al. (2018)</a>,
              <a href="https://pubmed.ncbi.nlm.nih.gov/20804598/" className="underline hover:text-primary ml-1" target="_blank" rel="noopener noreferrer">Hansen et al. (2010)</a>
            </p>
          </div>
          
          <div className="bg-muted p-3 md:p-4 rounded-lg mt-3 md:mt-4 border border-border">
            <h4 className="font-bold text-foreground mb-3 text-sm md:text-base">Visualisering av taurinets effekt på fettsyreforbrenning</h4>
            <img 
              src={taurineGraph} 
              alt="Graf som viser taurinets effekt på enzymaktivitet ved ulike pH-nivåer" 
              className="w-full rounded-lg mb-3"
            />
            <p className="text-xs md:text-sm text-muted-foreground">
              <span className="font-semibold text-primary">Figur fra Hansen et al. (2010):</span> Grafen viser hvordan enzymene som bryter ned fettsyrer (MCAD, LCAD, SCAD) blir betydelig mer aktive ved høyere pH-nivåer når taurin er tilstede.
            </p>
          </div>
        </div>

        <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
          <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">L-Tyrosine – 3 000 mg</h3>
          <div className="bg-muted p-3 md:p-4 rounded-lg border border-border mb-3">
            <h4 className="font-bold text-primary mb-2 text-sm md:text-base">Markedssnitt: ~1 227 mg. Peveo: 3 000 mg.</h4>
            <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
              Selv om markedssnittet er ~1 227 mg, er dette langt under effektiv dose. Ifølge <a href="https://pubmed.ncbi.nlm.nih.gov/25797188/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">Jongkees et al. (2015)</a> kreves det minimum 2g for kognitiv effekt under stress. Militær forskning av <a href="https://pubmed.ncbi.nlm.nih.gov/25600522/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">Attipoe et al. (2015)</a> bekrefter at tyrosin holder hodet skarpt når kroppen er under akutt belastning. Vi gir deg 3 000mg.
            </p>
          </div>
          <p className="mb-3 text-sm md:text-base text-muted-foreground">
            L-Tyrosine er forløperen for nevrotransmitterne dopamin, adrenalin og noradrenalin. 
            Virker spesielt godt ved akutt stress og kognitiv belastning ved å støtte katekolaminsyntesen i hjernen.
          </p>
          <div className="bg-muted p-3 md:p-4 rounded-lg mb-3">
            <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Forskningsresultater:</h4>
            <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
              <li>Forbedret mental skarphet og fokus under stress</li>
              <li>Økt kognitiv fleksibilitet og arbeidsminne</li>
              <li>Bedre humør og motivasjon ved akutt belastning</li>
              <li>Støtter dopamin- og noradrenalinsyntese</li>
            </ul>
            <p className="mt-2 text-sm text-primary">
              Kilder: 
              <a href="https://pubmed.ncbi.nlm.nih.gov/25797188/" className="underline hover:text-primary ml-1" target="_blank" rel="noopener noreferrer">Jongkees et al. (2015)</a>,
              <a href="https://pubmed.ncbi.nlm.nih.gov/25600522/" className="underline hover:text-primary ml-1" target="_blank" rel="noopener noreferrer">Attipoe et al. (2015)</a>
            </p>
          </div>

          <div className="bg-muted p-3 md:p-4 rounded-lg mb-3 border border-border">
            <h4 className="font-bold text-foreground mb-3 text-sm md:text-base">Hvordan tyrosin påvirker hjernen</h4>
            <img 
              src={tyrosineGraph} 
              alt="Diagram som viser tyrosins transport over blod-hjerne-barrieren og konvertering til dopamin og noradrenalin" 
              className="w-full rounded-lg mb-3 bg-white p-2"
            />
            <p className="text-xs md:text-sm text-muted-foreground">
              <span className="font-semibold text-primary">Figur fra Fernstrom & Fernstrom (2007):</span> Tyrosin-tilgjengelighet i hjernen er avgjørende for hvor mye dopamin og noradrenalin som kan dannes i aktive nevroner.
            </p>
          </div>

          <div className="bg-muted p-3 md:p-4 rounded-lg border border-border">
            <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">L-Tyrosine vs. N-Acetyl L-Tyrosine:</h4>
            <p className="text-xs md:text-sm text-muted-foreground">
              L-Tyrosine er den mest biotilgjengelige formen av Tyrosine. N-Acetyl L-Tyrosine konverteres svært ineffektivt 
              til Tyrosine i kroppen og kan ikke øke plasma-tyrosinnivåer i betydelig grad.
            </p>
          </div>
        </div>
      </div>

      {/* Bitter Orange */}
      <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
        <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">Bitter Orange Extract – 1 053 mg</h3>
        <p className="mb-3 text-sm md:text-base text-muted-foreground">
          Kilde til p-synefrin. Øker fettoksidasjon og metabolisme uten de kardiovaskulære bivirkningene du ser med efedrin-lignende stoffer.
        </p>
        <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
          <li>Økt termogenese og energiforbruk</li>
          <li>Forbedret fettforbrenning under trening</li>
          <li>Mild stimulering uten koffeinubehag</li>
        </ul>
        <p className="mt-2 text-sm text-primary">
          Kilder: 
          <a href="https://pubmed.ncbi.nlm.nih.gov/22429674/" className="underline hover:text-primary ml-1" target="_blank" rel="noopener noreferrer">Stohs et al. (2012)</a>,
          <a href="https://pubmed.ncbi.nlm.nih.gov/33578380/" className="underline hover:text-primary ml-1" target="_blank" rel="noopener noreferrer">Ruiz-Moreno et al. (2021)</a>
        </p>
      </div>

      {/* Minor ingredients */}
      <div className="grid md:grid-cols-3 gap-3 md:gap-6">
        <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
          <h3 className="text-base md:text-xl font-semibold text-primary mb-2">Niacin – 16 mg</h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Niacin (vitamin B3) spiller en kritisk rolle i energimetabolismen og bidrar til normal funksjon av nervesystemet.
          </p>
        </div>

        <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
          <h3 className="text-base md:text-xl font-semibold text-primary mb-2">Pink Himalayan Sea Salt – 500 mg</h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Bidrar til elektrolyttbalanse og optimal muskelfunksjon under intensiv trening.
          </p>
        </div>

        <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
          <h3 className="text-base md:text-xl font-semibold text-primary mb-2">Sodium (200 mg) & Potassium (190 mg)</h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Essensielle elektrolytter for optimal nevromuskulær funksjon og hydrering under trening.
          </p>
        </div>
      </div>

      {/* Hva vi IKKE har */}
      <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
        <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">Hva vi IKKE har i – og hvorfor</h3>
        <div className="space-y-3 md:space-y-4">
          <div className="bg-muted p-2 md:p-3 rounded-lg border border-border">
            <h4 className="font-bold text-foreground text-sm md:text-base">Beta-Alanine</h4>
            <p className="text-xs md:text-sm text-muted-foreground">Krever daglig inntak over flere uker for å ha effekt (som kreatin). Gir bare akutt kløe i huden, som mange forveksler med «at produktet virker». En 2,85% forbedring krever 179g akkumulert. Kjøp det separat hvis du vil – men det hører ikke hjemme i en pre-workout.</p>
          </div>
          <div className="bg-muted p-2 md:p-3 rounded-lg border border-border">
            <h4 className="font-bold text-foreground text-sm md:text-base">BCAA</h4>
            <p className="text-xs md:text-sm text-muted-foreground">Bortkastet hvis du spiser nok protein. BCAA er allerede tilstede i whey og mat. Å tilsette det i en PWO er ren markedsføring uten substans.</p>
          </div>
          <div className="bg-muted p-2 md:p-3 rounded-lg border border-border">
            <h4 className="font-bold text-foreground text-sm md:text-base">Leucin</h4>
            <p className="text-xs md:text-sm text-muted-foreground">Bra post-workout, men pre-workout kan det hemme glykogen-til-glukose-konvertering og redusere ytelse.</p>
          </div>
          <div className="bg-muted p-2 md:p-3 rounded-lg border border-border">
            <h4 className="font-bold text-foreground text-sm md:text-base">L-Carnitine</h4>
            <p className="text-xs md:text-sm text-muted-foreground">Ikke biotilgjengelig oralt. Å ta denne ingrediensen oralt er sløsing med penger.</p>
          </div>
          <div className="bg-muted p-2 md:p-3 rounded-lg border border-border">
            <h4 className="font-bold text-foreground text-sm md:text-base">Koffein</h4>
            <p className="text-xs md:text-sm text-muted-foreground">Toleranse bygges fort, ødelegger søvn, og reduserer koffeineffekten på konkurransedagen. Kjøp koffeintabletter separat for bedre kontroll.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default OtherIngredientsSection;
